import { HugeiconsIcon } from "@hugeicons/react";
import { FolderSyncIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { ButtonLoading } from "~/components/ui/button-loading";
import { CardLoadingIndicator } from "~/components/ui/card-loading-indicator";
import { CopyInput } from "~/components/ui/copy-input";
import {
	FormCard,
	FormCardBody,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardHeaderTitle,
} from "~/components/ui/form-card";
import { Label } from "~/components/ui/label";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute(
	"/_app/podcasts/$channelUid/settings/directories",
)({
	component: Page,
	async loader({ context, params }) {
		await Promise.all([
			context.queryClient.ensureQueryData(
				context.trpc.directory.listByChannel.queryOptions({
					channelUid: params.channelUid,
				}),
			),
		]);
	},
});

function Page() {
	const { channelUid } = Route.useParams();
	const trpc = useTRPC();
	const { t } = useLingui();

	const { data: channelDirectories } = useQuery(
		trpc.directory.listByChannel.queryOptions({ channelUid }),
	);

	const { mutate: refreshDirectories, isPending: isRefreshingDirectories } =
		useMutation(
			trpc.directory.refresh.mutationOptions({
				onSuccess: () => {
					toast.success(
						t`Directories refreshed successfully. It may take a few minutes for changes to appear in all feeds.`,
					);
				},
			}),
		);

	return (
		<div className="grid gap-6 w-full px-4">
			<FormCard>
				<FormCardHeader>
					<div>
						<FormCardHeaderTitle>
							<Trans>Directories RSS Feeds</Trans>
						</FormCardHeaderTitle>
						<FormCardHeaderSubtitle>
							<Trans>
								Get the RSS feeds for your podcast in different directories.
							</Trans>
						</FormCardHeaderSubtitle>
					</div>

					<div className="ml-auto">
						<ButtonLoading
							variant="outline"
							isLoading={isRefreshingDirectories}
							disabled={isRefreshingDirectories}
							onClick={() => refreshDirectories({ channelUid })}
						>
							{!isRefreshingDirectories && (
								<HugeiconsIcon icon={FolderSyncIcon} />
							)}
							<Trans>Refresh Directories</Trans>
						</ButtonLoading>
					</div>
				</FormCardHeader>
				<FormCardBody>
					{channelDirectories === undefined && <CardLoadingIndicator />}
					{channelDirectories?.map((channelDirectory) => (
						<div
							key={channelDirectory.directoryID}
							className="grid gap-2 not-last:pb-4 not-last:border-b"
						>
							<div className="flex gap-3 items-center">
								<div className="flex flex-col gap-2 w-56">
									<div className="flex-1">
										<div className="flex items-center gap-4 px-1 pt-1.5 text-left text-sm">
											<Avatar className="size-12 rounded-none">
												<AvatarImage
													alt={channelDirectory.directoryName}
													src={channelDirectory.iconURL}
													className="object-contain"
												/>
												<AvatarFallback className="uppercase">
													{channelDirectory.directoryName.slice(0, 1)}
												</AvatarFallback>
											</Avatar>
											<div className="grid flex-1 text-left text-sm leading-tight">
												<div className="flex items-center">
													<Label>{channelDirectory.directoryName}</Label>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="ml-auto w-full">
									<Label>
										<Trans>Podcast RSS feed URL</Trans>
									</Label>
									<CopyInput
										defaultValue={channelDirectory.directoryRSS}
										className="w-full"
										placeholder="https://example.com/rss"
									/>
								</div>
							</div>
						</div>
					))}
				</FormCardBody>
			</FormCard>
		</div>
	);
}
